/**
 * Fullscreen Alerts — cinematic floating overlay. No background.
 *
 * Gameplay stays fully visible at all times. Everything floats: shockwave
 * ring expands from center, particle burst follows, username slams onto
 * screen from scale(2.5), event line + secondary stat reveal under it,
 * accent underline sweeps. Card redemption swaps to a vertical-stacked
 * layout — card enters from the bottom, flips forward at t=1s, flips
 * back to the back face just before the exit fade.
 *
 * Same queue/phase pattern as AlertBoxWidget. Sound resolution: per-event
 * token > 'default' > silence. Card redemption sound is delayed 1s to
 * sync with the flip reveal.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';
import {
  DEFAULT_MESSAGES,
  PLATFORM_COLORS,
  RARITY_COLORS,
  RARITY_ORDER,
  type AlertPalette,
  type Rarity,
  derivePalette,
  eventKeyCascade,
  extractTemplateVars,
  extractUsername,
  hexToRgb,
  passesMinFilters,
  pickFrom,
  renderTemplate,
} from '../alertHelpers';

// HypeVault pink — used for tips. Orange — used for raids.
const TIP_PALETTE: AlertPalette = {
  base: '#f655a6',
  light: '#f988bf',
  dark: '#bf2e76',
  dim: 'rgba(246,85,166,0.4)',
};
const RAID_PALETTE: AlertPalette = {
  base: '#f97316',
  light: '#fb923c',
  dark: '#c2410c',
  dim: 'rgba(249,115,22,0.4)',
};

// Cinematic subtitle line per event token. Token-first so the user can
// override via fields.messageTemplates later in the resolver.
const EVENT_LINES: Record<string, string> = {
  follow: 'NOW FOLLOWING',
  sub: 'JUST SUBSCRIBED',
  'sub.gift': 'GIFTED A SUBSCRIPTION',
  'sub.yt-member': 'BECAME A MEMBER',
  'sub.gift.yt-member': 'GIFTED A MEMBERSHIP',
  'sub.kick-sub': 'JUST SUBSCRIBED ON KICK',
  'sub.gift.kick-sub': 'GIFTED A KICK SUB',
  'sub.hypevault': 'SUBSCRIBED TO THE CHANNEL',
  'sub.gift.hypevault': 'GIFTED A HYPEVAULT SUB',
  cheer: 'CHEERED 💎',
  'cheer.kick': 'SENT KICKS 💚',
  tip: 'TIPPED 💜',
  raid: 'RAIDED THE STREAM 🚀',
  alertPurchase: 'BOUGHT AN ALERT',
  cardRedemption: 'PLAYED A CARD',
  superChat: 'SENT A SUPERCHAT 💰',
  superSticker: 'SENT A SUPER STICKER 🌟',
  channelPointRedemption: 'REDEEMED {title} ⭐',
};

interface SecondaryStat {
  /** Formatted big-number value, e.g. "1,234" */
  value: string;
  /** Optional label appended to value, e.g. "viewers", "bits" */
  label: string;
}

interface QueueItem {
  id: string;
  eventToken: string;
  username: string;
  /** Pre-resolved event subtitle (e.g. "JUST SUBSCRIBED"). */
  eventLine: string;
  duration: number;
  variant: 'standard' | 'card';
  // Card data
  rarity: Rarity | null;
  cardImageUrl: string | null;
  cardName: string | null;
  cardDescription: string | null;
  // Secondary stat (raid/tip/cheer/superChat)
  secondary: SecondaryStat | null;
  palette: AlertPalette;
  soundUrl: string | null;
}

interface Particle {
  angle: number;
  distance: number;
  size: number;
  delay: number;
  duration: number;
  color: string;
}

type RainVariant = 'hvcoin' | 'bits' | 'kick' | 'dollar';

interface RainIcon {
  id: number;
  variant: RainVariant;
  /** Horizontal start position, 0–100 (percent of viewport). */
  x: number;
  /** Vertical start offset above the viewport (px, negative). */
  startY: number;
  /** Icon size in px. */
  size: number;
  /** Horizontal drift in px over the fall (±). */
  drift: number;
  /** Final rotation in deg (±). */
  rot: number;
  /** Fall duration in seconds. */
  duration: number;
  /** Stagger delay in seconds. */
  delay: number;
}

function rainVariantFor(eventToken: string): RainVariant | null {
  if (eventToken === 'tip') return 'hvcoin';
  if (eventToken === 'cheer') return 'bits';
  if (eventToken === 'cheer.kick') return 'kick';
  if (eventToken === 'superChat' || eventToken === 'superSticker') return 'dollar';
  return null;
}

function buildRain(seed: number, variant: RainVariant): RainIcon[] {
  const rand = seededRand(seed);
  const count = 25 + Math.floor(rand() * 11); // 25–35
  const isCoin = variant === 'hvcoin';
  return Array.from({ length: count }).map((_, i) => ({
    id: i,
    variant,
    x: rand() * 100,
    startY: -40 + rand() * 30,
    size: isCoin ? 24 + rand() * 12 : 20 + rand() * 12,
    drift: (rand() - 0.5) * 40,
    rot: (rand() - 0.5) * 360,
    duration: 2 + rand() * 1.5,
    delay: rand() * 1.5,
  }));
}

// Splitmix32 seeded off the alert id so re-renders don't reshuffle.
function seededRand(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x9e3779b9) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 16), 0x21f0aaad);
    t = Math.imul(t ^ (t >>> 15), 0x735a2d97);
    return ((t ^ (t >>> 15)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function particleColors(palette: AlertPalette): string[] {
  const rgb = hexToRgb(palette.base);
  const whiteish =
    rgb &&
    `rgba(${Math.min(255, rgb.r + 60)},${Math.min(255, rgb.g + 60)},${Math.min(255, rgb.b + 60)},1)`;
  return [palette.base, palette.light, whiteish ?? '#ffffff', '#ffffff', palette.dim];
}

export function FullscreenAlertsWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const durationMs = Math.max(2000, Number(fields.duration ?? 8) * 1000);
  const enabledEvents = useMemo(
    () => new Set((fields.enabledEvents as string[] | null | undefined) ?? []),
    [fields.enabledEvents],
  );
  const hasFilter = (fields.enabledEvents as string[] | undefined)?.length;
  const minTip = Number(fields.minTipCoins ?? 0);
  const minCheer = Number(fields.minCheerBits ?? 0);
  const minSuper = Number(fields.minSuperChatCents ?? 0);
  const minRarity = (fields.minRarityForCardAlert as Rarity | undefined) ?? 'common';
  const soundEnabled = (fields.soundEnabled as boolean | undefined) ?? true;
  const soundVolume = Math.max(0, Math.min(1, Number(fields.soundVolume ?? 0.6)));
  const soundUrls = (fields.soundUrls as Record<string, string> | undefined) ?? {};
  const platformColors = (fields.platformColors as Record<string, string> | undefined) ?? {};
  const messageTemplates = (fields.messageTemplates as Record<string, string> | undefined) ?? {};

  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [current, setCurrent] = useState<QueueItem | null>(null);
  /**
   * 'in'         — playing entry + linger.
   * 'flipback'   — card variant only: card flips back to its back face.
   * 'out'        — root fades + scales out + blurs.
   */
  const [phase, setPhase] = useState<'in' | 'flipback' | 'out'>('in');
  const lastEventId = useRef<string | null>(null);

  // ──────────────────────────────────────────────────────────────────
  // Ingest
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastEventId.current) return;
    lastEventId.current = latestEvent.id;

    const cascade = eventKeyCascade(latestEvent);
    const token = cascade[0]!;
    if (hasFilter && !cascade.some((k) => enabledEvents.has(k))) return;
    if (!passesMinFilters(latestEvent, minTip, minCheer, minSuper)) return;

    if (latestEvent.type === 'cardRedemption') {
      const rarity = (latestEvent.cardRarity ?? 'common') as Rarity;
      if (RARITY_ORDER.indexOf(rarity) < RARITY_ORDER.indexOf(minRarity)) return;
    }

    // Subtitle line — streamer override > our cinematic line > generic message.
    // renderTemplate runs on every branch so {title}/{cost}/{amount}/etc.
    // substitute correctly regardless of which source the line came from.
    // A previous version only rendered the streamer-override path, which
    // meant DEFAULT_MESSAGES with placeholders leaked through as literal
    // "{title}" text on screen — bit channel point alerts.
    const overridden = pickFrom(cascade, messageTemplates);
    const rawLine =
      overridden ??
      pickFrom(cascade, EVENT_LINES) ??
      DEFAULT_MESSAGES[token] ??
      token.toUpperCase();
    const eventLine = renderTemplate(rawLine, extractTemplateVars(latestEvent)).toUpperCase();

    // Palette
    let palette: AlertPalette;
    let rarity: Rarity | null = null;
    if (latestEvent.type === 'cardRedemption') {
      rarity = (latestEvent.cardRarity ?? 'common') as Rarity;
      const rc = RARITY_COLORS[rarity];
      palette = { base: rc.base, light: rc.base, dark: rc.base, dim: rc.glow };
    } else if (latestEvent.type === 'tip' || latestEvent.type === 'tip_received') {
      palette = TIP_PALETTE;
    } else if (latestEvent.type === 'raid') {
      palette = RAID_PALETTE;
    } else if (latestEvent.type === 'superChat' || latestEvent.type === 'superSticker') {
      palette = PLATFORM_COLORS.youtube;
    } else {
      const override = platformColors[latestEvent.platform];
      palette = override ? derivePalette(override) : PLATFORM_COLORS[latestEvent.platform];
    }

    // Secondary stat
    let secondary: SecondaryStat | null = null;
    if (latestEvent.type === 'raid') {
      secondary = { value: latestEvent.viewers.toLocaleString(), label: 'viewers' };
    } else if (latestEvent.type === 'tip') {
      secondary = { value: latestEvent.coins.toLocaleString(), label: 'coins' };
    } else if (latestEvent.type === 'tip_received') {
      secondary = { value: `$${(latestEvent.amountCents / 100).toFixed(2)}`, label: '' };
    } else if (latestEvent.type === 'superChat' || latestEvent.type === 'superSticker') {
      secondary = { value: `$${(latestEvent.amountCents / 100).toFixed(2)}`, label: '' };
    } else if (latestEvent.type === 'cheer') {
      secondary = {
        value: latestEvent.bits.toLocaleString(),
        label: latestEvent.platform === 'kick' ? 'kicks' : 'bits',
      };
    } else if (latestEvent.type === 'channelPointRedemption') {
      secondary = { value: latestEvent.rewardCost.toLocaleString(), label: 'pts' };
    }

    const resolvedSound = pickFrom(cascade, soundUrls) ?? soundUrls.default ?? null;

    setQueue((q) => [
      ...q,
      {
        id: latestEvent.id,
        eventToken: token,
        username: extractUsername(latestEvent),
        eventLine,
        duration: durationMs,
        variant: latestEvent.type === 'cardRedemption' ? 'card' : 'standard',
        rarity,
        cardImageUrl:
          latestEvent.type === 'cardRedemption' ? (latestEvent.cardImageUrl ?? null) : null,
        cardName: latestEvent.type === 'cardRedemption' ? latestEvent.cardName : null,
        cardDescription:
          latestEvent.type === 'cardRedemption' ? (latestEvent.cardDescription ?? null) : null,
        secondary,
        palette,
        soundUrl: resolvedSound,
      },
    ]);
  }, [
    latestEvent,
    enabledEvents,
    hasFilter,
    minTip,
    minCheer,
    minSuper,
    minRarity,
    durationMs,
    messageTemplates,
    platformColors,
    soundUrls,
  ]);

  // ──────────────────────────────────────────────────────────────────
  // Drain queue → current. Sound fires here.
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (current || queue.length === 0) return;
    const next = queue[0]!;
    setQueue((q) => q.slice(1));
    setCurrent(next);
    setPhase('in');
    if (soundEnabled && next.soundUrl) {
      const url = next.soundUrl;
      // Card flip lands at t=1s — line up the sound with the reveal.
      const playDelay = next.variant === 'card' ? 1000 : 0;
      const fire = () => {
        try {
          const audio = new Audio(url);
          audio.volume = soundVolume;
          void audio.play().catch(() => {});
        } catch {
          /* ignore */
        }
      };
      if (playDelay > 0) setTimeout(fire, playDelay);
      else fire();
    }
  }, [current, queue, soundEnabled, soundVolume]);

  // ──────────────────────────────────────────────────────────────────
  // Phase progression: in → (flipback for card) → out → unmount
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!current) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    // The fade-out animation runs 0.8s — kick it off so it lands right at duration.
    const FADE_MS = 800;
    const FLIP_BACK_MS = 800;
    if (current.variant === 'card') {
      const flipBackAt = Math.max(0, current.duration - FADE_MS - FLIP_BACK_MS);
      timers.push(setTimeout(() => setPhase('flipback'), flipBackAt));
    }
    timers.push(setTimeout(() => setPhase('out'), current.duration - FADE_MS));
    timers.push(setTimeout(() => setCurrent(null), current.duration));
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [current]);

  // ──────────────────────────────────────────────────────────────────
  // Particles — deterministic per alert id
  // ──────────────────────────────────────────────────────────────────
  const particles = useMemo<Particle[]>(() => {
    if (!current) return [];
    const rand = seededRand(hashString(current.id));
    const colors = particleColors(current.palette);
    return Array.from({ length: 40 }).map((_, i) => {
      const baseAngle = (i / 40) * 360;
      return {
        angle: baseAngle + rand() * 9,
        distance: 200 + rand() * 300,
        size: 3 + rand() * 5,
        delay: rand() * 0.2,
        duration: 0.9 + rand() * 0.5,
        color: colors[i % colors.length]!,
      };
    });
  }, [current?.id, current?.palette]);

  // ──────────────────────────────────────────────────────────────────
  // Rain — falling icons for tip / cheer / cheer.kick
  // ──────────────────────────────────────────────────────────────────
  const rain = useMemo<RainIcon[]>(() => {
    if (!current) return [];
    const variant = rainVariantFor(current.eventToken);
    if (!variant) return [];
    return buildRain(hashString(current.id) ^ 0x9c2a, variant);
  }, [current?.id, current?.eventToken]);

  if (!current) return null;

  const cssVars: React.CSSProperties = {
    ['--fs-accent' as string]: current.palette.base,
    ['--fs-accent-light' as string]: current.palette.light,
    ['--fs-accent-dark' as string]: current.palette.dark,
    ['--fs-accent-dim' as string]: current.palette.dim,
  };

  return (
    <div className={`fs-root fs-variant-${current.variant} phase-${phase}`} style={cssVars}>
      {rain.length > 0 && (
        <div className="fs-rain">
          {rain.map((r) => (
            <div
              key={r.id}
              className="fs-rain-icon"
              style={{
                left: `${r.x}%`,
                top: `${r.startY}px`,
                width: r.size,
                height: r.size,
                fontSize: r.size * 0.4,
                animation: `fs-rain-fall ${r.duration}s ${r.delay}s ease-in forwards`,
                ['--drift' as string]: `${r.drift}px`,
                ['--rot' as string]: `${r.rot}deg`,
              }}
            >
              {r.variant === 'hvcoin' && <div className="fs-icon-body fs-icon-hvcoin">HV</div>}
              {r.variant === 'bits' && (
                <div className="fs-icon-body fs-icon-bits">
                  <span className="fs-icon-sparkle" />
                </div>
              )}
              {r.variant === 'kick' && <div className="fs-icon-body fs-icon-kick">K</div>}
              {r.variant === 'dollar' && <div className="fs-icon-body fs-icon-dollar">$</div>}
            </div>
          ))}
        </div>
      )}

      {current.variant === 'standard' && <div className="fs-shockwave" />}

      <div className={`fs-particles fs-particles-${current.variant}`}>
        {particles.map((p, i) => {
          const rad = (p.angle * Math.PI) / 180;
          const px = Math.cos(rad) * p.distance;
          const py = Math.sin(rad) * p.distance;
          const style: React.CSSProperties = {
            width: p.size,
            height: p.size,
            background: p.color,
            animation: `fs-particle ${p.duration}s ${p.delay}s ease-out forwards`,
            ['--px' as string]: `${px}px`,
            ['--py' as string]: `${py}px`,
          };
          return <span key={i} className="fs-particle" style={style} />;
        })}
      </div>

      {current.variant === 'card' ? (
        <CardStack item={current} duration={current.duration} />
      ) : (
        <StandardStack item={current} />
      )}

      <FullscreenStyles />
    </div>
  );
}

function StandardStack({ item }: { item: QueueItem }) {
  return (
    <div className="fs-stack fs-stack-standard">
      <div className="fs-username fs-username-standard">{item.username}</div>
      <div className="fs-underline" />
      <div className="fs-event-line">{item.eventLine}</div>
      {item.secondary && (
        <div className="fs-stat">
          <span className="fs-stat-value">{item.secondary.value}</span>
          {item.secondary.label && <span className="fs-stat-label">{item.secondary.label}</span>}
        </div>
      )}
    </div>
  );
}

function CardStack({ item, duration }: { item: QueueItem; duration: number }) {
  const rarity = item.rarity ?? 'common';
  const rc = RARITY_COLORS[rarity];
  const FADE_MS = 800;
  const FLIP_BACK_MS = 800;
  const flipBackDelayMs = Math.max(0, duration - FADE_MS - FLIP_BACK_MS);
  // Pipe the flip-back delay through CSS custom prop so the second flip
  // animation knows when to fire. Without this it'd kick off at the same
  // time as the forward flip and the reveal never lands.
  const flipVars: React.CSSProperties = {
    ['--fs-flip-back-delay' as string]: `${flipBackDelayMs}ms`,
    ['--fs-flip-back-duration' as string]: `${FLIP_BACK_MS}ms`,
  };
  return (
    <div className="fs-stack fs-stack-card">
      <div className="fs-flip" style={flipVars}>
        <div className="fs-flip-inner">
          <div className="fs-flip-face fs-flip-back">
            <div className="fs-flip-back-art" />
            <div className="fs-flip-back-emblem">HV</div>
          </div>
          <div className="fs-flip-face fs-flip-front">
            {item.cardImageUrl ? (
              <img alt="" src={item.cardImageUrl} className="fs-flip-art" />
            ) : (
              <div className="fs-flip-art-fallback">🃏</div>
            )}
            <div className="fs-flip-overlay">
              <div className="fs-flip-rarity">{rc.label}</div>
            </div>
            <div className="fs-flip-shine" />
          </div>
        </div>
        <div className="fs-flip-glow" />
      </div>
      <div className="fs-username fs-username-card">{item.username}</div>
      <div className="fs-event-line fs-event-line-card">{item.eventLine}</div>
      <div className="fs-card-name">{item.cardName}</div>
      {item.cardDescription && <div className="fs-card-desc">{item.cardDescription}</div>}
    </div>
  );
}

function FullscreenStyles() {
  return (
    <style>{`
      .fs-root {
        position: relative;
        width: 100%;
        height: 100%;
        font-family: 'Inter', system-ui, sans-serif;
        pointer-events: none;
        overflow: hidden;
        color: white;
      }
      /* Exit fade applies to whole root — both variants. */
      .fs-root.phase-out {
        animation: fs-exit 0.8s ease-in forwards;
      }
      @keyframes fs-exit {
        0%   { opacity: 1;   transform: scale(1);    filter: blur(0px); }
        40%  { opacity: 0.8; transform: scale(1.02); filter: blur(0px); }
        100% { opacity: 0;   transform: scale(1.04); filter: blur(4px); }
      }

      /* ────────────── Shockwave ring (standard only) ────────────── */
      .fs-shockwave {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 0;
        height: 0;
        border-radius: 50%;
        border: 3px solid var(--fs-accent);
        box-shadow:
          0 0 40px 8px var(--fs-accent),
          0 0 80px 20px var(--fs-accent-dim);
        pointer-events: none;
        opacity: 0;
      }
      .fs-root.phase-in .fs-shockwave,
      .fs-root.phase-flipback .fs-shockwave {
        animation: fs-shockwave 0.55s ease-out forwards;
      }
      @keyframes fs-shockwave {
        0%   { width: 0;      height: 0;      opacity: 1; }
        100% { width: 1400px; height: 1400px; opacity: 0; }
      }

      /* ────────────── Particles ────────────── */
      .fs-particles {
        position: absolute;
        pointer-events: none;
        z-index: 1;
        inset: 0;
      }
      /* Card variant: particles anchor higher (where the card sits in the stack)
         so the burst originates from the card center, not the viewport center. */
      .fs-particles-standard .fs-particle { top: 50%; left: 50%; }
      .fs-particles-card .fs-particle     { top: 32%; left: 50%; }
      .fs-particle {
        position: absolute;
        border-radius: 50%;
        opacity: 0;
        box-shadow: 0 0 12px currentColor;
        transform: translate(-50%, -50%) translate(0,0) scale(1);
      }
      @keyframes fs-particle {
        0%   { opacity: 1; transform: translate(-50%, -50%) translate(0,0) scale(1); }
        70%  { opacity: 0.85; }
        100% { opacity: 0; transform: translate(-50%, -50%) translate(var(--px,0), var(--py,0)) scale(0); }
      }

      /* ────────────── Vertical stack layout ────────────── */
      .fs-stack {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        z-index: 2;
        padding: 40px;
        text-align: center;
      }
      .fs-stack-card { gap: 14px; padding-top: 80px; }

      /* ────────────── Username (slam) ────────────── */
      .fs-username {
        font-weight: 900;
        letter-spacing: -3px;
        line-height: 1;
        color: #fff;
        opacity: 0;
        word-break: break-word;
        max-width: 92%;
        text-shadow:
          0 0 60px var(--fs-accent),
          0 0 120px var(--fs-accent-dim),
          0 6px 20px rgba(0,0,0,0.9);
      }
      .fs-username-standard { font-size: 96px; }
      .fs-username-card     { font-size: 72px; }
      .fs-root.phase-in .fs-username-standard,
      .fs-root.phase-flipback .fs-username-standard {
        animation: fs-name-slam 0.4s 0.15s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .fs-root.phase-in .fs-username-card,
      .fs-root.phase-flipback .fs-username-card {
        animation: fs-name-slam 0.4s 2s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      @keyframes fs-name-slam {
        0%   { opacity: 0; transform: scale(2.5); filter: blur(8px); }
        60%  { opacity: 1; transform: scale(0.95); filter: blur(0); }
        80%  { opacity: 1; transform: scale(1.02); filter: blur(0); }
        100% { opacity: 1; transform: scale(1); filter: blur(0); }
      }

      /* ────────────── Accent underline (standard) ────────────── */
      .fs-underline {
        width: 0;
        height: 2px;
        background: linear-gradient(90deg, transparent, var(--fs-accent), var(--fs-accent-light), var(--fs-accent), transparent);
        box-shadow: 0 0 18px var(--fs-accent);
        opacity: 0;
        border-radius: 2px;
      }
      .fs-root.phase-in .fs-underline,
      .fs-root.phase-flipback .fs-underline {
        animation: fs-line-sweep 0.6s 0.2s ease-out forwards;
      }
      @keyframes fs-line-sweep {
        0%   { width: 0;     opacity: 1; }
        80%  { width: 600px; opacity: 1; }
        100% { width: 600px; opacity: 0.4; }
      }

      /* ────────────── Event subtitle line ────────────── */
      .fs-event-line {
        font-size: 24px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 4px;
        color: var(--fs-accent);
        text-shadow: 0 0 20px var(--fs-accent-dim);
        opacity: 0;
      }
      .fs-root.phase-in .fs-event-line,
      .fs-root.phase-flipback .fs-event-line {
        animation: fs-event-in 0.5s 0.4s ease-out both;
      }
      /* Card variant runs the same animation but delayed so it lands after the flip + username. */
      .fs-root.phase-in .fs-event-line-card,
      .fs-root.phase-flipback .fs-event-line-card {
        animation: fs-event-in 0.5s 2.3s ease-out both;
      }
      @keyframes fs-event-in {
        0%   { opacity: 0; transform: translateY(16px) scaleX(0.85); letter-spacing: 8px; }
        100% { opacity: 1; transform: translateY(0) scaleX(1); letter-spacing: 4px; }
      }

      /* ────────────── Secondary stat (big number) ────────────── */
      .fs-stat {
        display: flex;
        align-items: baseline;
        justify-content: center;
        gap: 14px;
        width: 100%;
        text-align: center;
        opacity: 0;
      }
      .fs-root.phase-in .fs-stat,
      .fs-root.phase-flipback .fs-stat {
        animation: fs-stat-in 0.4s 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      @keyframes fs-stat-in {
        0%   { opacity: 0; transform: scale(0.5); }
        70%  { transform: scale(1.08); }
        100% { opacity: 1; transform: scale(1); }
      }
      .fs-stat-value {
        font-size: 72px;
        font-weight: 900;
        color: var(--fs-accent);
        line-height: 1;
        letter-spacing: -2px;
        text-shadow: 0 0 40px var(--fs-accent);
      }
      .fs-stat-label {
        font-size: 22px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 3px;
        color: rgba(255,255,255,0.7);
      }

      /* ────────────── Card name + description (card variant) ────────────── */
      .fs-card-name {
        font-size: 28px;
        font-weight: 800;
        color: var(--fs-accent);
        letter-spacing: -0.5px;
        text-shadow: 0 0 22px var(--fs-accent-dim);
        opacity: 0;
        max-width: 80%;
        word-break: break-word;
      }
      .fs-root.phase-in .fs-card-name,
      .fs-root.phase-flipback .fs-card-name {
        animation: fs-rise 0.5s 2.5s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .fs-card-desc {
        font-size: 16px;
        font-style: italic;
        line-height: 1.4;
        color: rgba(255,255,255,0.7);
        opacity: 0;
        max-width: 60%;
      }
      .fs-root.phase-in .fs-card-desc,
      .fs-root.phase-flipback .fs-card-desc {
        animation: fs-rise 0.5s 2.7s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      @keyframes fs-rise {
        from { opacity: 0; transform: translateY(14px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      /* ────────────── Card flip ────────────── */
      .fs-flip {
        position: relative;
        width: 300px;
        aspect-ratio: 5 / 7;
        perspective: 1500px;
        opacity: 0;
      }
      .fs-root.phase-in .fs-flip,
      .fs-root.phase-flipback .fs-flip {
        animation: fs-card-enter 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
      }
      @keyframes fs-card-enter {
        0%   { opacity: 0; transform: translateY(120%) rotate(-8deg); }
        60%  { opacity: 1; transform: translateY(-8px) rotate(1deg); }
        80%  { transform: translateY(4px) rotate(-0.5deg); }
        100% { opacity: 1; transform: translateY(0) rotate(0); }
      }
      .fs-flip-inner {
        position: absolute;
        inset: 0;
        transform-style: preserve-3d;
        transform: rotateY(180deg);
        /* Two animations: forward flip at t=1s, backward flip at the
           component-supplied delay just before exit. Both forwards-filled
           so the second takes over cleanly from the first's resting state. */
        animation:
          fs-flip-forward 1s 1s cubic-bezier(0.55,0.05,0.2,1) forwards,
          fs-flip-back var(--fs-flip-back-duration, 0.8s) var(--fs-flip-back-delay, 99999s) ease-in forwards;
      }
      @keyframes fs-flip-forward {
        from { transform: rotateY(180deg); }
        to   { transform: rotateY(0deg);   }
      }
      @keyframes fs-flip-back {
        from { transform: rotateY(0deg);   }
        to   { transform: rotateY(180deg); }
      }
      .fs-flip-face {
        position: absolute;
        inset: 0;
        border-radius: 18px;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;
        overflow: hidden;
        box-shadow:
          0 22px 50px -12px rgba(0,0,0,0.7),
          0 0 0 1px rgba(255,255,255,0.08) inset;
      }
      .fs-flip-back {
        background:
          radial-gradient(circle at 50% 35%, var(--fs-accent) 0%, rgba(0,0,0,0.7) 70%),
          repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 6px, transparent 6px 12px);
        border: 2px solid var(--fs-accent);
        display: grid;
        place-items: center;
        transform: rotateY(180deg);
      }
      .fs-flip-back-art {
        position: absolute;
        inset: 14px;
        border: 1px dashed rgba(255,255,255,0.25);
        border-radius: 10px;
      }
      .fs-flip-back-emblem {
        position: relative;
        font-weight: 900;
        font-size: 60px;
        letter-spacing: -3px;
        color: white;
        text-shadow: 0 0 24px var(--fs-accent-dim), 0 2px 6px rgba(0,0,0,0.7);
      }
      .fs-flip-front {
        background: #0b0918;
        border: 2px solid var(--fs-accent);
      }
      .fs-flip-art {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
      .fs-flip-art-fallback {
        width: 100%;
        height: 100%;
        display: grid;
        place-items: center;
        font-size: 110px;
        background: radial-gradient(circle at 50% 40%, var(--fs-accent), rgba(0,0,0,0.7));
      }
      .fs-flip-overlay {
        position: absolute;
        left: 0; right: 0; top: 0;
        padding: 14px 14px 22px;
        background: linear-gradient(180deg, rgba(0,0,0,0.85), transparent);
        display: flex;
      }
      .fs-flip-rarity {
        font-size: 13px;
        font-weight: 800;
        letter-spacing: 1.6px;
        text-transform: uppercase;
        padding: 4px 10px;
        border-radius: 5px;
        background: var(--fs-accent);
        color: #0b0918;
        box-shadow: 0 0 14px var(--fs-accent-dim);
      }
      .fs-flip-shine {
        position: absolute;
        inset: 0;
        background: linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.35) 50%, transparent 60%);
        transform: translateX(-120%);
        pointer-events: none;
      }
      .fs-root.phase-in .fs-flip-shine,
      .fs-root.phase-flipback .fs-flip-shine {
        animation: fs-flip-shine 1.4s 2.1s ease-out forwards;
      }
      @keyframes fs-flip-shine {
        from { transform: translateX(-120%); }
        to   { transform: translateX(120%); }
      }
      .fs-flip-glow {
        position: absolute;
        inset: -16px;
        border-radius: 24px;
        box-shadow: 0 0 80px 8px var(--fs-accent-dim);
        opacity: 0;
        pointer-events: none;
        z-index: -1;
      }
      .fs-root.phase-in .fs-flip-glow,
      .fs-root.phase-flipback .fs-flip-glow {
        animation: fs-flip-glow 1.6s 1.8s ease-out forwards;
      }
      @keyframes fs-flip-glow {
        0%   { opacity: 0; }
        40%  { opacity: 1; }
        100% { opacity: 0.55; }
      }

      /* ────────────── Falling icon rain (tip / cheer / cheer.kick) ────────────── */
      .fs-rain {
        position: absolute;
        inset: 0;
        overflow: hidden;
        pointer-events: none;
        z-index: 0;
      }
      .fs-rain-icon {
        position: absolute;
        pointer-events: none;
        will-change: transform, opacity;
      }
      .fs-icon-body {
        width: 100%;
        height: 100%;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 900;
        font-family: 'Inter', system-ui, sans-serif;
        letter-spacing: -0.06em;
      }
      .fs-icon-hvcoin {
        border-radius: 50%;
        background:
          radial-gradient(circle at 30% 28%, rgba(255,255,255,0.55) 0%, transparent 38%),
          radial-gradient(circle at 50% 55%, #fbbf24 0%, #f59e0b 65%, #b45309 100%);
        box-shadow:
          0 0 8px rgba(245, 158, 11, 0.6),
          inset 0 -2px 3px rgba(120, 53, 15, 0.5),
          0 0 0 1px rgba(180, 83, 9, 0.5);
        color: #7c2d12;
        text-shadow: 0 1px 0 rgba(255,255,255,0.45);
      }
      .fs-icon-bits {
        background: linear-gradient(135deg, #a855f7 0%, #7c3aed 60%, #6d28d9 100%);
        box-shadow:
          0 0 10px rgba(168, 85, 247, 0.7),
          inset 0 -2px 3px rgba(76, 29, 149, 0.55),
          0 0 0 1px rgba(91, 33, 182, 0.6);
        border-radius: 4px;
        transform: rotate(45deg);
      }
      .fs-icon-sparkle {
        position: absolute;
        top: 15%;
        left: 18%;
        width: 28%;
        height: 22%;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.75);
        filter: blur(1px);
      }
      .fs-icon-kick {
        border-radius: 50%;
        background:
          radial-gradient(circle at 32% 28%, rgba(255,255,255,0.5) 0%, transparent 38%),
          radial-gradient(circle at 50% 55%, #53fc18 0%, #3ac710 65%, #1d7e09 100%);
        box-shadow:
          0 0 10px rgba(83, 252, 24, 0.65),
          inset 0 -2px 3px rgba(26, 138, 8, 0.55),
          0 0 0 1px rgba(26, 138, 8, 0.6);
        color: #052e0a;
        text-shadow: 0 1px 0 rgba(255,255,255,0.4);
      }
      /* SuperChat / SuperSticker — green coin with gold $ */
      .fs-icon-dollar {
        border-radius: 50%;
        background:
          radial-gradient(circle at 30% 28%, rgba(255,255,255,0.55) 0%, transparent 38%),
          radial-gradient(circle at 50% 55%, #15803d 0%, #14532d 70%, #052e16 100%);
        box-shadow:
          0 0 10px rgba(34, 197, 94, 0.6),
          inset 0 -2px 3px rgba(5, 46, 22, 0.55),
          0 0 0 1px rgba(5, 46, 22, 0.6);
        color: #fde047;
        text-shadow:
          0 0 4px rgba(253, 224, 71, 0.7),
          0 1px 0 rgba(0, 0, 0, 0.45);
      }
      @keyframes fs-rain-fall {
        0%   { transform: translateY(0) translateX(0) rotate(0deg); opacity: 1; }
        80%  { opacity: 1; }
        100% { transform: translateY(1200px) translateX(var(--drift, 0px)) rotate(var(--rot, 0deg)); opacity: 0; }
      }
    `}</style>
  );
}
